import { z } from "zod";
import { isOneOf } from "@remi/ts-utils";

export interface UpsellReasonOption {
  value: string;
  title: string;
  description?: string;
  badge?: string;
}

/**
 * Use this if you need to loop through all the options (includes legacy values for DB compatibility)
 */
export const UpsellReasonValues = [
  "REMI_BID_WAS_TOO_EXPENSIVE",
  "REMI_BID_LESS_EXPENSIVE_HOMEOWNER_COMMITTED_TO_ANOTHER_ROOFER",
  "HOMEOWNER_WAITING",
  "COMPETITOR_PERSONAL_CONNECTION",
  "COMPETITOR_PAID_DEPOSIT",
  "COMPETITOR_LOWER_PRICE",
  "COMPETITOR_REMODEL",
  "COMPETITOR_INSURANCE_APPROVAL",
  "ROOF_WORK_NOT_NEEDED",
  "HOMEOWNER_UNRESPONSIVE",
  "PATCH_WORK",
  "ESCALATED_TO_PARTNER",
  "FAILED_CREDIT",
  "NO_CONTACT",
  "HOMEOWNER_UNRESPONSIVE_POST_QUOTE",
  "HOMEOWNER_UNRESPONSIVE_POST_CONTRACT_SENT",
  "HOMEOWNER_UNRESPONSIVE_POST_SIGNATURE",
  "NO_CONTRACTOR_COVERAGE",
  "DISQUALIFIED",
  "OTHER",
] as const;

/**
 * Use this if you need to zod parse it
 */
export const UpsellReasonSchema = z.enum(UpsellReasonValues);
/**
 * Use this if you need to use the literal value
 */
export const UpsellReason = UpsellReasonSchema.enum;
/**
 * Use this if you need to the type
 */
export type UpsellReason = z.infer<typeof UpsellReasonSchema>;
/**
 * Use this if you need to display a human-readable name
 */

/**
 * A subset of the upsell reasons that are currently active in app (matches Figma design)
 */
export const UpsellReasonActiveValues = [
  "ESCALATED_TO_PARTNER",
  "FAILED_CREDIT",
  "ROOF_WORK_NOT_NEEDED",
  "COMPETITOR_INSURANCE_APPROVAL",
  "COMPETITOR_LOWER_PRICE",
  "COMPETITOR_PAID_DEPOSIT",
  "COMPETITOR_PERSONAL_CONNECTION",
  "PATCH_WORK",
  "COMPETITOR_REMODEL",
  "NO_CONTACT",
  "HOMEOWNER_UNRESPONSIVE_POST_QUOTE",
  "HOMEOWNER_UNRESPONSIVE_POST_CONTRACT_SENT",
  "HOMEOWNER_UNRESPONSIVE_POST_SIGNATURE",
  "HOMEOWNER_WAITING",
  "NO_CONTRACTOR_COVERAGE",
  "OTHER",
] as const satisfies UpsellReason[];

export const UpsellReasonDtcOnlyValues = [UpsellReason.DISQUALIFIED] as const satisfies UpsellReason[];

export const UpsellReasonsDisplayName: Record<UpsellReason, string> = {
  REMI_BID_WAS_TOO_EXPENSIVE: "Remi bid more expensive",
  REMI_BID_LESS_EXPENSIVE_HOMEOWNER_COMMITTED_TO_ANOTHER_ROOFER: "Remi is cheaper, but committed to another roofer",
  HOMEOWNER_WAITING: "Waiting to do project",
  COMPETITOR_PERSONAL_CONNECTION: "Personal connection",
  COMPETITOR_PAID_DEPOSIT: "Paid deposit",
  COMPETITOR_LOWER_PRICE: "Lower price",
  COMPETITOR_REMODEL: "Remodel",
  COMPETITOR_INSURANCE_APPROVAL: "Insurance approval",
  ROOF_WORK_NOT_NEEDED: "No work needed",
  HOMEOWNER_UNRESPONSIVE: "Unresponsive",
  PATCH_WORK: "Patch work",
  ESCALATED_TO_PARTNER: "Escalated",
  FAILED_CREDIT: "Failed credit",
  NO_CONTACT: "No contact",
  HOMEOWNER_UNRESPONSIVE_POST_QUOTE: "Post quote",
  HOMEOWNER_UNRESPONSIVE_POST_CONTRACT_SENT: "Post contract sent",
  HOMEOWNER_UNRESPONSIVE_POST_SIGNATURE: "Post signature",
  NO_CONTRACTOR_COVERAGE: "No contractor coverage",
  DISQUALIFIED: "Disqualified",
  OTHER: "Other",
} as const;

/**
 * Enhanced descriptions for upsell reasons
 */
const UpsellReasonDescriptions: Partial<Record<UpsellReason, string>> = {
  ESCALATED_TO_PARTNER: "The homeowner is escalated by the cost and is going back to partner",
  FAILED_CREDIT: "The homeowner was unable to get a loan and can not pay for the project",
  ROOF_WORK_NOT_NEEDED: "The homeowner does not need any work done",
  COMPETITOR_INSURANCE_APPROVAL: "The competitor has already been approved by the homeowner's insurance company",
  COMPETITOR_LOWER_PRICE: "The homeowner chose the competitor due to a more affordable overall price.",
  COMPETITOR_PAID_DEPOSIT: "The homeowner has already committed to the competitor by paying a deposit",
  COMPETITOR_PERSONAL_CONNECTION: "The homeowner knows the contractor they are working with personally",
  PATCH_WORK: "The homeowner only wants patch work which falls outside of Remi's scope of work",
  COMPETITOR_REMODEL: "The homeowner is remodeling their home and the changes fall outside of Remi's scope of work",
  NO_CONTACT: "We were unable to get ahold of the homeowner ever.",
  HOMEOWNER_UNRESPONSIVE_POST_QUOTE: "The homeowner received the quote but was unresponsive after",
  HOMEOWNER_UNRESPONSIVE_POST_CONTRACT_SENT: "The homeowner was unresponsive after the contract was sent",
  HOMEOWNER_UNRESPONSIVE_POST_SIGNATURE: "The homeowner signed the contract but was unresponsive after",
  HOMEOWNER_WAITING: "The homeowner is waiting to do project for a variety of different reasons",
  NO_CONTRACTOR_COVERAGE: "Remi has no contractor available to cover the homeowner's area",
} as const;

/**
 * Badge categories for upsell reasons
 */
const UpsellReasonBadges: Partial<Record<UpsellReason, string>> = {
  COMPETITOR_INSURANCE_APPROVAL: "Competitor",
  COMPETITOR_LOWER_PRICE: "Competitor",
  COMPETITOR_PAID_DEPOSIT: "Competitor",
  COMPETITOR_PERSONAL_CONNECTION: "Competitor",
  PATCH_WORK: "Outside of scope",
  COMPETITOR_REMODEL: "Outside of scope",
  NO_CONTACT: "Unresponsive",
  HOMEOWNER_UNRESPONSIVE_POST_QUOTE: "Unresponsive",
  HOMEOWNER_UNRESPONSIVE_POST_CONTRACT_SENT: "Unresponsive",
  HOMEOWNER_UNRESPONSIVE_POST_SIGNATURE: "Unresponsive",
} as const;

/**
 * Enhanced select options for the active upsell reasons (generated from actual enum values)
 */
export const UpsellReasonEnhancedOptions: UpsellReasonOption[] = UpsellReasonActiveValues.map((value) => ({
  value,
  title: UpsellReasonsDisplayName[value],
  description: UpsellReasonDescriptions[value],
  badge: UpsellReasonBadges[value],
}));

export const UpsellReasonWithLegacyEnhancedOptions: UpsellReasonOption[] = UpsellReasonValues.filter(
  (value) => !isOneOf(value, UpsellReasonDtcOnlyValues),
).map((value) => ({
  value,
  title: UpsellReasonsDisplayName[value],
  description: UpsellReasonDescriptions[value],
  badge: UpsellReasonBadges[value],
}));
